import { InputNumber, Table } from 'antd';
import Column from 'antd/es/table/Column';
import { formatVND } from '../../helpers/common';
import { ChiTietLuongThemGioItem } from '../../models/luong/LuongThemGio';

const toNumber = (value: any) => Number(value ?? 0);

export interface FlatLuongThemGioRow {
    type: 'grand-summary' | 'group-summary' | 'employee';
    groupIndex?: number;
    romanIndex?: string;
    groupName?: string;
    stt?: number;
    originalIndex?: number;
    item?: ChiTietLuongThemGioItem;
    themGioNgayThuong?: number;
    themGioNgayNghi?: number;
    themGioNgayLe?: number;
    tienNgayThuong?: number;
    tienNgayNghi?: number;
    tienNgayLe?: number;
    soTienDuocNhan?: number;
}

const ROMAN_NUMERALS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

export const buildFlatRows = (
    dataSource: ChiTietLuongThemGioItem[]
): FlatLuongThemGioRow[] => {
    if (!dataSource || dataSource.length === 0) return [];

    const rows: FlatLuongThemGioRow[] = [];

    // 1. Grand Summary
    rows.push({
        type: 'grand-summary',
        themGioNgayThuong: dataSource.reduce((s, x) => s + toNumber(x.themGioNgayThuong), 0),
        themGioNgayNghi: dataSource.reduce((s, x) => s + toNumber(x.themGioNgayNghi), 0),
        themGioNgayLe: dataSource.reduce((s, x) => s + toNumber(x.themGioNgayLe), 0),
        tienNgayThuong: dataSource.reduce((s, x) => s + toNumber(x.tienNgayThuong), 0),
        tienNgayNghi: dataSource.reduce((s, x) => s + toNumber(x.tienNgayNghi), 0),
        tienNgayLe: dataSource.reduce((s, x) => s + toNumber(x.tienNgayLe), 0),
        soTienDuocNhan: dataSource.reduce((s, x) => s + toNumber(x.soTienDuocNhan), 0),
    });

    // 2. Group by IdNhom
    const groupOrder = [3, 7, 1];
    const groupMap = new Map<number, { items: { item: ChiTietLuongThemGioItem; origIdx: number }[]; name: string }>();

    dataSource.forEach((item, origIdx) => {
        let nhomId = item.idNhom;
        if (!nhomId || nhomId === 0) {
            nhomId = item.isQuanLy ? 3 : 1;
        }

        let groupName = item.tenNhom;
        if (!groupName) {
            groupName = nhomId === 3 ? 'Người quản lý' : nhomId === 7 ? 'Trưởng Ban kiểm soát' : 'Người lao động';
        }

        if (!groupMap.has(nhomId)) {
            groupMap.set(nhomId, { items: [], name: groupName });
        }
        groupMap.get(nhomId)!.items.push({ item, origIdx });
    });

    const sortedGroupKeys = Array.from(groupMap.keys()).sort((a, b) => {
        const idxA = groupOrder.indexOf(a);
        const idxB = groupOrder.indexOf(b);
        const orderA = idxA === -1 ? 999 : idxA;
        const orderB = idxB === -1 ? 999 : idxB;
        return orderA - orderB;
    });

    let currentStt = 1;

    sortedGroupKeys.forEach((key, gIdx) => {
        const grp = groupMap.get(key)!;
        const roman = gIdx < ROMAN_NUMERALS.length ? ROMAN_NUMERALS[gIdx] : `${gIdx + 1}`;

        // Group summary row
        rows.push({
            type: 'group-summary',
            groupIndex: key,
            romanIndex: roman,
            groupName: grp.name,
            themGioNgayThuong: grp.items.reduce((s, x) => s + toNumber(x.item.themGioNgayThuong), 0),
            themGioNgayNghi: grp.items.reduce((s, x) => s + toNumber(x.item.themGioNgayNghi), 0),
            themGioNgayLe: grp.items.reduce((s, x) => s + toNumber(x.item.themGioNgayLe), 0),
            tienNgayThuong: grp.items.reduce((s, x) => s + toNumber(x.item.tienNgayThuong), 0),
            tienNgayNghi: grp.items.reduce((s, x) => s + toNumber(x.item.tienNgayNghi), 0),
            tienNgayLe: grp.items.reduce((s, x) => s + toNumber(x.item.tienNgayLe), 0),
            soTienDuocNhan: grp.items.reduce((s, x) => s + toNumber(x.item.soTienDuocNhan), 0),
        });

        // Employee rows
        grp.items.forEach(({ item, origIdx }) => {
            rows.push({
                type: 'employee',
                stt: currentStt++,
                originalIndex: origIdx,
                item,
                themGioNgayThuong: toNumber(item.themGioNgayThuong),
                themGioNgayNghi: toNumber(item.themGioNgayNghi),
                themGioNgayLe: toNumber(item.themGioNgayLe),
                tienNgayThuong: toNumber(item.tienNgayThuong),
                tienNgayNghi: toNumber(item.tienNgayNghi),
                tienNgayLe: toNumber(item.tienNgayLe),
                soTienDuocNhan: toNumber(item.soTienDuocNhan),
            });
        });
    });

    return rows;
};

const TableChiTietLuongThemGio = ({
    loading,
    dataSource,
    isEdit,
    onChangeItem
}: {
    loading: boolean;
    dataSource: ChiTietLuongThemGioItem[];
    isEdit?: boolean;
    onChangeItem?: (index: number, field: string, value: any) => void;
}) => {
    const showNgayThuong = dataSource.some(
        x => toNumber(x.themGioNgayThuong) !== 0 || toNumber(x.tienNgayThuong) !== 0,
    );

    const showNgayNghi = dataSource.some(
        x => toNumber(x.themGioNgayNghi) !== 0 || toNumber(x.tienNgayNghi) !== 0,
    );

    const showNgayLe = dataSource.some(
        x => toNumber(x.themGioNgayLe) !== 0 || toNumber(x.tienNgayLe) !== 0,
    );

    const flatRows = buildFlatRows(dataSource);

    const getRowClassName = (record: FlatLuongThemGioRow) => {
        if (record.type === 'grand-summary') return 'table-row-grand-summary';
        if (record.type === 'group-summary') return 'table-row-group-summary';
        return '';
    };

    return (
        <div className="table-salary-wrapper">
            <style>{`
                .table-row-grand-summary td {
                    background-color: #fff9e6 !important;
                    font-weight: bold;
                }
                .table-row-group-summary td {
                    background-color: #e6f7ff !important;
                    font-weight: bold;
                }
            `}</style>
            <Table<FlatLuongThemGioRow>
                sticky
                pagination={false}
                loading={loading}
                bordered
                rowKey={(_rec, idx) => `row-${idx}`}
                rowClassName={getRowClassName}
                locale={{ emptyText: 'Không có dữ liệu' }}
                dataSource={flatRows}
            >
                <Column
                    title="STT"
                    width={60}
                    fixed="left"
                    align="center"
                    render={(_v, r: FlatLuongThemGioRow) => {
                        if (r.type === 'grand-summary') return '';
                        if (r.type === 'group-summary') return <b>{r.romanIndex}</b>;
                        return r.stt;
                    }}
                />
                <Column
                    title="Mã NV"
                    dataIndex="ma"
                    width={70}
                    fixed="left"
                    align="center"
                    render={(_v, r: FlatLuongThemGioRow) => {
                        if (r.type === 'employee') return r.item?.ma ?? '';
                        return '';
                    }}
                />
                <Column
                    title="Họ và tên"
                    dataIndex="hoTen"
                    width={220}
                    fixed="left"
                    render={(_v, r: FlatLuongThemGioRow) => {
                        if (r.type === 'grand-summary') return <b>Tổng cộng</b>;
                        if (r.type === 'group-summary') return <b>{r.groupName}</b>;
                        return r.item?.hoTen ?? '';
                    }}
                />
                <Column
                    title="Chức vụ"
                    dataIndex="tenChucVu"
                    width={120}
                    render={(_v, r: FlatLuongThemGioRow) => {
                        if (r.type === 'employee') return r.item?.tenChucVu ?? '';
                        return '';
                    }}
                />
                <Column
                    title="Phòng ban"
                    dataIndex="tenDonVi"
                    width={160}
                    render={(_v, r: FlatLuongThemGioRow) => {
                        if (r.type === 'employee') return r.item?.tenDonVi ?? '';
                        return '';
                    }}
                />
                <Column
                    title="Mức lương theo HĐLĐ/Bảng lương"
                    dataIndex="mucLuongTheoHopDong"
                    width={110}
                    align="right"
                    render={(_v, r: FlatLuongThemGioRow) => {
                        if (r.type === 'employee') return formatVND(r.item?.mucLuongTheoHopDong ?? 0);
                        return '';
                    }}
                />
                <Column
                    title="Tiền lương cơ bản một giờ"
                    dataIndex="luongCBMotGio"
                    width={110}
                    align="right"
                    render={(_v, r: FlatLuongThemGioRow) => {
                        if (r.type === 'employee') return formatVND(r.item?.luongCBMotGio ?? 0);
                        return '';
                    }}
                />
                {showNgayThuong && (
                    <Column
                        title="Tổng số giờ làm thêm ngày thường"
                        dataIndex="themGioNgayThuong"
                        width={85}
                        align="right"
                        render={(_v, r: FlatLuongThemGioRow) => {
                            if (r.type === 'grand-summary' || r.type === 'group-summary') {
                                return <b>{r.themGioNgayThuong ?? 0}</b>;
                            }
                            return r.item?.themGioNgayThuong ?? 0;
                        }}
                    />
                )}
                {showNgayNghi && (
                    <Column
                        title="Tổng số giờ làm thêm ngày nghỉ"
                        dataIndex="themGioNgayNghi"
                        width={85}
                        align="right"
                        render={(_v, r: FlatLuongThemGioRow) => {
                            if (r.type === 'grand-summary' || r.type === 'group-summary') {
                                return <b>{r.themGioNgayNghi ?? 0}</b>;
                            }
                            return r.item?.themGioNgayNghi ?? 0;
                        }}
                    />
                )}
                {showNgayLe && (
                    <Column
                        title="Tổng số giờ làm thêm ngày lễ"
                        dataIndex="themGioNgayLe"
                        width={85}
                        align="right"
                        render={(_v, r: FlatLuongThemGioRow) => {
                            if (r.type === 'grand-summary' || r.type === 'group-summary') {
                                return <b>{r.themGioNgayLe ?? 0}</b>;
                            }
                            return r.item?.themGioNgayLe ?? 0;
                        }}
                    />
                )}
                {showNgayThuong && (
                    <Column
                        title="Tiền lương làm thêm ngày thường"
                        dataIndex="tienNgayThuong"
                        width={100}
                        align="right"
                        render={(_v, r: FlatLuongThemGioRow) => {
                            if (r.type === 'grand-summary' || r.type === 'group-summary') {
                                return <b>{formatVND(r.tienNgayThuong ?? 0)}</b>;
                            }
                            return formatVND(r.item?.tienNgayThuong ?? 0);
                        }}
                    />
                )}
                {showNgayNghi && (
                    <Column
                        title="Tiền lương làm thêm ngày nghỉ"
                        dataIndex="tienNgayNghi"
                        width={100}
                        align="right"
                        render={(_v, r: FlatLuongThemGioRow) => {
                            if (r.type === 'grand-summary' || r.type === 'group-summary') {
                                return <b>{formatVND(r.tienNgayNghi ?? 0)}</b>;
                            }
                            return formatVND(r.item?.tienNgayNghi ?? 0);
                        }}
                    />
                )}
                {showNgayLe && (
                    <Column
                        title="Tiền lương làm thêm ngày lễ"
                        dataIndex="tienNgayLe"
                        width={100}
                        align="right"
                        render={(_v, r: FlatLuongThemGioRow) => {
                            if (r.type === 'grand-summary' || r.type === 'group-summary') {
                                return <b>{formatVND(r.tienNgayLe ?? 0)}</b>;
                            }
                            return formatVND(r.item?.tienNgayLe ?? 0);
                        }}
                    />
                )}
                <Column
                    title="Thành tiền"
                    dataIndex="soTienDuocNhan"
                    width={110}
                    align="right"
                    render={(_v, r: FlatLuongThemGioRow) => {
                        if (r.type === 'grand-summary' || r.type === 'group-summary') {
                            return <b>{formatVND(r.soTienDuocNhan ?? 0)}</b>;
                        }
                        return isEdit ? (
                            <InputNumber
                                min={0}
                                step={1000}
                                value={r.item?.soTienDuocNhan}
                                style={{ width: '100%' }}
                                formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                parser={(val) => Number(`${val}`.replace(/,/g, '')) || 0}
                                onChange={(val) => onChangeItem && r.originalIndex !== undefined && onChangeItem(r.originalIndex, 'soTienDuocNhan', Number(val) || 0)}
                            />
                        ) : (
                            formatVND(r.item?.soTienDuocNhan ?? 0)
                        );
                    }}
                />
            </Table>
        </div>
    );
};

export default TableChiTietLuongThemGio;